/**
 * Small app preferences kept beside the platform's saves, under `arrow:v1:*`, parsed
 * defensively (a bad value reads as unset; nothing here throws):
 *
 * - `arrow:v1:last-level`: the level the player last opened a daily at, so Today and a
 *   bare `/daily/:n` link open where they left off.
 * - `arrow:v1:paused:<slot>`: when a game was last paused (left, hidden, closed), so
 *   coming back can take the time away out of Flow's inactivity window.
 */
import { isLevel, type Level } from '@arrow/puzzle-schema';

import { browserStore, type KeyValueStore } from '../storage/kv.ts';

const V = 'arrow:v1:';
export const prefKeys = {
	lastLevel: `${V}last-level`,
	pausedAt: (slot: string) => `${V}paused:${slot}`,
} as const;

export interface Prefs {
	lastLevel(): Level | null;
	setLastLevel(level: Level): void;
	/** Epoch ms the game in `slot` was paused, or null. */
	pausedAt(slot: string): number | null;
	setPausedAt(slot: string, at: number): void;
	clearPausedAt(slot: string): void;
}

export function createPrefs(store: KeyValueStore = browserStore()): Prefs {
	return {
		lastLevel() {
			const v = store.get(prefKeys.lastLevel);
			return isLevel(v) ? v : null;
		},
		setLastLevel(level) {
			if (isLevel(level)) store.set(prefKeys.lastLevel, level);
		},
		pausedAt(slot) {
			const v = Number(store.get(prefKeys.pausedAt(slot)));
			return Number.isFinite(v) && v > 0 ? v : null;
		},
		setPausedAt(slot, at) {
			if (Number.isFinite(at) && at > 0) store.set(prefKeys.pausedAt(slot), String(Math.round(at)));
		},
		clearPausedAt(slot) {
			store.remove(prefKeys.pausedAt(slot));
		},
	};
}

let shared: Prefs | null = null;
/** The app's prefs (localStorage). */
export function prefs(): Prefs {
	shared ??= createPrefs();
	return shared;
}
